import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';

export const SIDEBAR_WIDTH = 240;

const AppSidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/' },
    { label: 'Knowledge Base', icon: <MenuBookIcon fontSize="small" />, path: '/kb' },
    { label: 'Bank GICs', icon: <AccountBalanceIcon fontSize="small" />, path: '/gics' },
    ...(user?.role_id === 1 ? [{ label: 'Admin', icon: <AdminPanelSettingsIcon fontSize="small" />, path: '/admin' }] : []),
    { label: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings' },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <Box sx={{
      width: SIDEBAR_WIDTH,
      height: '100vh',
      bgcolor: '#1a1a2e',
      color: '#f7f3ee',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1200,
      borderRight: '1px solid #2d2d4e',
    }}>
      {/* Branding */}
      <Box
        sx={{ px: 3, py: 2.5, borderBottom: '1px solid #2d2d4e', cursor: 'pointer' }}
        onClick={() => navigate('/')}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{
            width: 36, height: 36, bgcolor: '#c9a84c', borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, flexShrink: 0,
          }}>🔐</Box>
          <Typography sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#f7f3ee',
            lineHeight: 1.2,
          }}>
            Vaults Portal
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: '0.62rem',
          color: '#f0e0b0',
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          pl: '46px',
        }}>
          {user?.name || 'Employee Hub'}
        </Typography>
      </Box>

      {/* Primary action */}
      <Box sx={{ px: 2, pt: 2.5, pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/entries/new')}
          sx={{
            bgcolor: '#c9a84c',
            color: '#1a1a2e',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#b8953e', transform: 'none', boxShadow: 'none' },
          }}
        >
          New Entry
        </Button>
      </Box>

      {/* Nav items */}
      <List sx={{ flex: 1, px: 1, pt: 0.5 }} disablePadding>
        {navItems.map(({ label, icon, path }) => {
          const active = isActive(path);
          return (
            <ListItemButton
              key={path}
              onClick={() => navigate(path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                color: active ? '#c9a84c' : '#9090b0',
                bgcolor: active ? 'rgba(201,168,76,0.12)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(201,168,76,0.08)', color: '#f7f3ee' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{icon}</ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* Logout */}
      <Box sx={{ p: 1.5, borderTop: '1px solid #2d2d4e' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: '#8a8070',
            '&:hover': { color: '#f7f3ee', bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );
};

export default AppSidebar;
